import { useMemo, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Combobox } from "@/components/flexkit/combobox/Combobox";
import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { workflowExecutionsUrl, workflowExecutionActionUrl } from "@/utils/automations";
import IconPageTextPieChart from "central-icons/IconPageTextPieChart";

type AutomationExecutionPageSelectorProps = {
    workflowExecutionId: string;
    actions: PlainWorkflowExecutionAction[];
};

type PageItem = {
    id: string;
    label: string;
    url: string;
};

export const AutomationExecutionPageSelector: FC<AutomationExecutionPageSelectorProps> = ({
    workflowExecutionId,
    actions,
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const summaryUrl = workflowExecutionsUrl({ id: workflowExecutionId });

    const pageItems: PageItem[] = useMemo(
        () => [
            { id: "summary", label: "Summary", url: summaryUrl },
            ...actions.map((action) => ({
                id: action.id,
                label: action.metadata?.actionName || "-",
                url: workflowExecutionActionUrl({
                    actionId: action.id,
                    executionId: workflowExecutionId,
                }),
            })),
        ],
        [actions, summaryUrl, workflowExecutionId],
    );

    const urlToLabel = useMemo(() => new Map(pageItems.map((item) => [item.url, item.label])), [pageItems]);

    const currentValue = pageItems.find((item) => item.url === location.pathname)?.url || summaryUrl;

    const handleValueChange = (url: string) => {
        navigate(url);
    };

    return (
        <Combobox value={currentValue} onValueChange={handleValueChange} filterPlaceholder="Search pages...">
            <Combobox.Value>
                {(url) => {
                    const isSummary = url === summaryUrl;
                    return (
                        <div className="flex items-center gap-2">
                            {isSummary && <IconPageTextPieChart size={16} />}
                            <Combobox.ValueLabel>{urlToLabel.get(url) || "Summary"}</Combobox.ValueLabel>
                        </div>
                    );
                }}
            </Combobox.Value>
            <Combobox.List items={pageItems} searchKeys={["label"]}>
                {(item) => (
                    <Combobox.ListItem key={item.id} value={item.url}>
                        {item.id === "summary" && (
                            <Combobox.ListItemLeadingIcon>
                                <IconPageTextPieChart size={16} />
                            </Combobox.ListItemLeadingIcon>
                        )}
                        <Combobox.ListItemTitle>{item.label}</Combobox.ListItemTitle>
                    </Combobox.ListItem>
                )}
            </Combobox.List>
            <Combobox.Footer>
                <span className="text-xs text-content-secondary">{actions.length} actions</span>
            </Combobox.Footer>
        </Combobox>
    );
};
